import { useState, useEffect, useCallback } from 'react'
import { ProductCategoryLabels, type Product, type BlockchainTransaction, type BlockchainHistoryResponse } from '@esf/shared'
import Modal from './Modal'
import { getApiBase } from '../utils/api'

interface ProductDetailModalProps {
  isOpen: boolean
  onClose: () => void
  product: Product | null
  onEdit: (product: Product) => void
  onDelete: (product: Product) => void
  onCreateWorkOrder: (product: Product) => void
  onRestore: (product: Product) => void
}

export default function ProductDetailModal({
  isOpen,
  onClose,
  product,
  onEdit,
  onDelete,
  onCreateWorkOrder,
  onRestore,
}: ProductDetailModalProps) {
  const [chainHistory, setChainHistory] = useState<BlockchainTransaction[]>([])
  const [chainLoading, setChainLoading] = useState(false)
  const [chainEnabled, setChainEnabled] = useState(true)
  const [chainSource, setChainSource] = useState<'cache' | 'chain'>('cache')
  const [chainError, setChainError] = useState<string | null>(null)

  // Fetch blockchain history (from cache by default, or from chain if verify=true)
  const fetchHistory = useCallback(async (verify = false) => {
    if (!product?.id) return

    setChainLoading(true)
    setChainError(null)
    try {
      const url = verify
        ? `${getApiBase()}/assets/${product.id}/blockchain-history?verify=true`
        : `${getApiBase()}/assets/${product.id}/blockchain-history`
      const res = await fetch(url, { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to fetch blockchain history')
      const data: BlockchainHistoryResponse = await res.json()
      setChainEnabled(data.enabled)
      setChainSource(data.source)
      setChainHistory(data.history || [])
      if (data.error) setChainError(data.error)
      if (data.message) setChainError(data.message)
    } catch (err) {
      setChainError((err as Error).message)
    } finally {
      setChainLoading(false)
    }
  }, [product?.id])

  // Fetch cached history when modal opens
  useEffect(() => {
    if (!isOpen || !product?.id) {
      setChainHistory([])
      setChainError(null)
      setChainSource('cache')
      return
    }
    fetchHistory(false)
  }, [isOpen, product?.id, fetchHistory])

  if (!product) return null

  const statusColors: Record<string, string> = {
    ACTIVE: 'bg-success-100 text-success-700 border-success-200',
    MAINTENANCE: 'bg-warning-100 text-warning-700 border-warning-200',
    DECOMMISSIONED: 'bg-gray-100 text-gray-700 border-gray-200',
    PENDING_INSTALLATION: 'bg-info-100 text-info-700 border-info-200',
    REPAIR_NEEDED: 'bg-error-100 text-error-700 border-error-200',
  }

  const conditionColors: Record<string, string> = {
    EXCELLENT: 'bg-success-100 text-success-700',
    GOOD: 'bg-info-100 text-info-700',
    FAIR: 'bg-warning-100 text-warning-700',
    POOR: 'bg-error-100 text-error-700',
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
      <h4 className="text-sm font-semibold text-gray-900 mb-3">{title}</h4>
      {children}
    </div>
  )

  const Field = ({ label, value, className }: { label: string; value: React.ReactNode; className?: string }) => (
    <div className={className}>
      <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{value || '-'}</dd>
    </div>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Product Details" maxWidth="2xl">
      <div className="space-y-6">
        {/* Deleted Banner */}
        {product.deletedAt && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <div>
                <p className="font-medium text-red-800">This product has been deleted</p>
                <p className="text-sm text-red-600">
                  Deleted on {new Date(product.deletedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
            </div>
            <button
              onClick={() => { onRestore(product); onClose(); }}
              className="btn btn-sm bg-success-600 text-white hover:bg-success-700"
            >
              Restore Product
            </button>
          </div>
        )}

        {/* Header with status */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">{product.name}</h3>
            <p className="text-sm text-gray-500 font-mono mt-1">{product.passportId}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {product.deletedAt ? (
              <span className="px-3 py-1 rounded-full text-sm font-medium border bg-red-100 text-red-700 border-red-200">
                Deleted
              </span>
            ) : (
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${statusColors[product.status]}`}>
                {product.status.replace(/_/g, ' ')}
              </span>
            )}
            {product.condition && !product.deletedAt && (
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${conditionColors[product.condition]}`}>
                {product.condition}
              </span>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        {product.deletedAt ? (
          <div className="flex gap-2 bg-gray-50 rounded-lg p-3">
            <button
              onClick={() => { onRestore(product); onClose(); }}
              className="btn btn-sm bg-success-600 text-white hover:bg-success-700 flex-1"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Restore Product
            </button>
            <a
              href={`#/public/passport/${product.passportId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm bg-esf-navy text-white hover:bg-esf-navy-dark flex-1"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              View Passport
            </a>
          </div>
        ) : (
          <div className="flex gap-2 bg-gray-50 rounded-lg p-3">
            <button
              onClick={() => { onClose(); onEdit(product); }}
              className="btn btn-sm btn-outline flex-1"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
            <button
              onClick={() => { onClose(); onCreateWorkOrder(product); }}
              className="btn btn-sm btn-secondary flex-1"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              Work Order
            </button>
            <button
              onClick={() => { onClose(); onDelete(product); }}
              className="btn btn-sm bg-error-50 text-error-600 hover:bg-error-100 border border-error-200"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
            <a
              href={`#/public/passport/${product.passportId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm bg-esf-navy text-white hover:bg-esf-navy-dark flex-1"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Public Passport
            </a>
          </div>
        )}

        {/* Basic Information */}
        <Section title="Basic Information">
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Category" value={
              <span className="badge bg-esf-navy/10 text-esf-navy">
                {ProductCategoryLabels[product.category]}
              </span>
            } />
            <Field label="Subcategory" value={product.subcategory} />
            <Field label="Model" value={product.model} />
            <Field label="Serial Number" value={product.serialNumber} />
            <Field label="GTIN" value={product.gtin} className="col-span-2" />
          </dl>
        </Section>

        {/* Location & Status */}
        <Section title="Location & Status">
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Installation Site" value={product.installationSite} />
            <Field label="Installation Date" value={formatDate(product.installationDate)} />
            <Field label="Last Inspection" value={formatDate(product.lastInspectionDate)} />
            <Field label="Next Maintenance" value={formatDate(product.nextMaintenanceDate)} />
            {product.location && (
              <Field
                label="Coordinates"
                value={`${product.location.lat?.toFixed(6)}, ${product.location.lng?.toFixed(6)}`}
                className="col-span-2"
              />
            )}
            {product.location?.address && (
              <Field label="Address" value={product.location.address} className="col-span-2" />
            )}
          </dl>
        </Section>

        {/* Digital Product Passport Data */}
        <Section title="Digital Product Passport (DPP)">
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Manufacturer" value={product.manufacturer} />
            <Field label="Country of Origin" value={product.manufacturerCountry} />
            <Field
              label="Materials"
              value={product.materials?.length ? (
                <div className="flex flex-wrap gap-1">
                  {product.materials.map((m, i) => {
                    const materialName = typeof m === 'string' ? m : (m as { name?: string }).name ?? 'Unknown';
                    return (
                      <span key={i} className="badge bg-gray-100 text-gray-700">{materialName}</span>
                    );
                  })}
                </div>
              ) : '-'}
              className="col-span-2"
            />
            <Field
              label="Certifications"
              value={product.certifications?.length ? (
                <div className="flex flex-wrap gap-1">
                  {product.certifications.map((c, i) => (
                    <span key={i} className="badge bg-success-100 text-success-700">{c}</span>
                  ))}
                </div>
              ) : '-'}
              className="col-span-2"
            />
          </dl>

          <div className="grid grid-cols-4 gap-4 mt-4 bg-gray-50 rounded-lg p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {product.carbonFootprint ? `${product.carbonFootprint.toLocaleString()}` : '-'}
              </div>
              <div className="text-xs text-gray-500 mt-1">kg CO2e</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {product.recyclabilityScore ?? '-'}
              </div>
              <div className="text-xs text-gray-500 mt-1">Recyclability %</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {product.repairabilityScore ?? '-'}
              </div>
              <div className="text-xs text-gray-500 mt-1">Repairability /10</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {product.expectedLifespan ?? '-'}
              </div>
              <div className="text-xs text-gray-500 mt-1">Years Lifespan</div>
            </div>
          </div>

          <dl className="grid grid-cols-2 gap-4 mt-4">
            <Field label="Warranty End" value={formatDate(product.warrantyEndDate)} />
          </dl>
        </Section>

        {/* Description & Notes */}
        {(product.description || product.notes) && (
          <Section title="Description & Notes">
            {product.description && (
              <div className="mb-4">
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Description</dt>
                <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{product.description}</dd>
              </div>
            )}
            {product.notes && (
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Internal Notes</dt>
                <dd className="mt-1 text-sm text-gray-700 whitespace-pre-wrap bg-yellow-50 rounded p-2 border border-yellow-100">
                  {product.notes}
                </dd>
              </div>
            )}
          </Section>
        )}

        {/* Tags */}
        {product.tags && product.tags.length > 0 && (
          <Section title="Tags">
            <div className="flex flex-wrap gap-2">
              {product.tags.map((tag, i) => (
                <span key={i} className="badge bg-esf-navy/10 text-esf-navy">{tag}</span>
              ))}
            </div>
          </Section>
        )}

        {/* Blockchain Chain History */}
        <Section title="Blockchain Chain History">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-100">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-indigo-900">ubloquity Blockchain</p>
                  <p className="text-xs text-indigo-600">
                    {!chainEnabled ? 'Blockchain Disabled' : chainSource === 'chain' ? 'Verified On-Chain' : 'Cached History'}
                  </p>
                </div>
              </div>
              {chainEnabled && !chainLoading && (
                <button
                  onClick={() => fetchHistory(true)}
                  className="btn btn-sm bg-indigo-100 text-indigo-700 hover:bg-indigo-200 border-0"
                  title="Verify directly on blockchain"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Verify
                </button>
              )}
            </div>

            {/* Loading State */}
            {chainLoading && (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                <span className="ml-2 text-sm text-indigo-600">Loading chain history...</span>
              </div>
            )}

            {/* Error/Warning State */}
            {chainError && !chainLoading && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700 mb-4">
                {chainError}
              </div>
            )}

            {/* Disabled State */}
            {!chainEnabled && !chainLoading && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
                Blockchain integration is currently disabled. Chain history will be available when enabled.
              </div>
            )}

            {/* Timeline */}
            {!chainLoading && chainEnabled && chainHistory.length > 0 && (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-indigo-200"></div>

                <div className="space-y-4">
                  {chainHistory.map((entry, index) => {
                    const actionConfig = {
                      CREATE: { icon: '➕', bg: 'bg-green-100', text: 'text-green-700', label: 'Created' },
                      UPDATE: { icon: '✏️', bg: 'bg-blue-100', text: 'text-blue-700', label: 'Updated' },
                      DELETE: { icon: '🗑️', bg: 'bg-red-100', text: 'text-red-700', label: 'Deleted' },
                    }[entry.action] || { icon: '📝', bg: 'bg-gray-100', text: 'text-gray-700', label: entry.action }

                    return (
                      <div key={entry.txId || index} className="relative pl-10">
                        {/* Timeline dot */}
                        <div className={`absolute left-2 w-5 h-5 rounded-full ${actionConfig.bg} flex items-center justify-center text-xs`}>
                          {actionConfig.icon}
                        </div>

                        <div className="bg-white/70 rounded-lg p-3 border border-indigo-100">
                          <div className="flex items-center justify-between mb-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${actionConfig.bg} ${actionConfig.text}`}>
                              {actionConfig.label}
                            </span>
                            <span className="text-xs text-gray-500">
                              {new Date(entry.timestamp).toLocaleString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>

                          {/* Transaction ID */}
                          <div className="flex items-center gap-2">
                            <code className="flex-1 text-xs bg-gray-50 rounded px-2 py-1 font-mono text-gray-600 truncate">
                              {entry.txId}
                            </code>
                            <button
                              onClick={() => navigator.clipboard.writeText(entry.txId)}
                              className="p-1 hover:bg-indigo-100 rounded transition-colors"
                              title="Copy transaction ID"
                            >
                              <svg className="w-3.5 h-3.5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>

                          {/* Changed fields (if UPDATE) */}
                          {entry.action === 'UPDATE' && entry.fields && entry.fields.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                              <p className="text-xs text-gray-500 mb-1">Changed fields:</p>
                              <div className="flex flex-wrap gap-1">
                                {entry.fields.slice(0, 5).map((field) => (
                                  <span key={field} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                    {field}
                                  </span>
                                ))}
                                {entry.fields.length > 5 && (
                                  <span className="px-1.5 py-0.5 text-gray-500 text-xs">
                                    +{entry.fields.length - 5} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Empty State */}
            {!chainLoading && chainEnabled && chainHistory.length === 0 && !chainError && (
              <div className="text-center py-4 text-sm text-gray-500">
                No blockchain transactions recorded yet.
              </div>
            )}
          </div>
        </Section>

        {/* Metadata */}
        <div className="text-xs text-gray-400 pt-4 border-t border-gray-100">
          <div className="flex justify-between">
            <span>Created: {formatDate(product.createdAt)}</span>
            <span>Updated: {formatDate(product.updatedAt)}</span>
          </div>
          <div className="mt-1 font-mono">ID: {product.id}</div>
        </div>
      </div>
    </Modal>
  )
}
